import React, { ReactNode } from "react";

import Badge from "antd/es/badge";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { FormattedMessage, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import TextWithLine from "@app/component/typography/TextWithLine";
import ReportType from "@app/enum/ReportType";
import StandardHeader from "@app/scene/reports/components/Header/StandardHeader";
import MapFilter, {
  getEntityTypeByMapFilter,
  getMapFilterDisplayName,
} from "@app/scene/reports/enums/MapFilter";
import ActivityCardData from "@app/scene/reports/store/ActivityCardData";
import DateRange from "@app/scene/reports/types/DateRange";
import colors from "@app/styles/_colors.module.scss";
import { isAfterStartOfToday, isBeforeEndOfToday } from "@app/util/dates";
import formatDateRange from "@app/util/stackRank/formatDateRange";

import headerStyles from "../../Header/Header.module.scss";
import TimeSelector from "../../Header/TimeSelector";
import ReliabilityColor from "../ReliabilityColor";

import styles from "./ActivityReportHeader.module.scss";

interface Props {
  activityCardData: ActivityCardData;
  children: ReactNode;
  dateRange: DateRange;
  filter: MapFilter;
}

const ActivityReportHeader: React.FC<Props> = ({
  activityCardData,
  children,
  dateRange,
  filter,
}) => {
  const intl = useIntl();
  const entityType = getEntityTypeByMapFilter(filter);
  const totalPins: number =
    entityType === EntityType.ACTIVITY
      ? activityCardData.totalActivities
      : entityType === EntityType.COMPANY
      ? activityCardData.totalCompanies
      : entityType === EntityType.PERSON
      ? activityCardData.totalPeople
      : 0;

  const timeSelectorOnlyCustom = ![
    MapFilter.AUTO_ACTIVITIES,
    MapFilter.COMPLETED_ACTIVITIES,
  ].includes(filter);

  return (
    <>
      <StandardHeader reportType={ReportType.ACTIVITY} />
      {children}
      <br />

      <TextWithLine>
        <div className={styles.mapHeaderContainer}>
          <TextWithInfo
            info={intl.formatMessage({
              id: "reports.map.header.activities.dateRangeFilter.notification",
              defaultMessage:
                "All records may not be visible on the map as several records may be pinned at the same location. Access all records below.",
              description: "Tooltip for the date range",
            })}
          >
            <FormattedMessage
              id="reports.map.header.activities"
              defaultMessage="{mapFilter} ({totalPins})"
              description="Title for Pins in Map section on Activity screen."
              values={{ mapFilter: getMapFilterDisplayName(intl, filter), totalPins }}
            />
          </TextWithInfo>
        </div>
      </TextWithLine>

      <Row className={styles.mapContainer}>
        <Col className={headerStyles.subTitle} lg={{ span: 16 }} xl={{ span: 18 }}>
          <div className={styles.dateRange}>
            <div>{formatDateRange(dateRange)}</div>
            <TimeSelector
              isDateDisabled={
                filter === MapFilter.UPCOMING_ACTIVITIES ? isBeforeEndOfToday : isAfterStartOfToday
              }
              onlyCustom={timeSelectorOnlyCustom}
              reportType={ReportType.ACTIVITY}
            />
          </div>
          {(filter === MapFilter.COMPANIES_OVER_CADENCE ||
            filter === MapFilter.PEOPLE_OVER_CADENCE) && (
            <div className={styles.activityReliabilityLegend}>
              <Badge
                color={colors.red100}
                text={intl.formatMessage({
                  id: "reports.map.header.activities.outOfCadence.legend.overdue",
                  defaultMessage: "Overdue",
                  description: "Tooltip for pins of entities with outOfCadence: true",
                })}
              />
              <Badge
                className={styles.dotWithBorder}
                color={colors.white}
                text={intl.formatMessage({
                  id: "reports.map.header.activities.outOfCadence.legend.upcoming",
                  defaultMessage: "Upcoming",
                  description: "Tooltip for pins of entities with outOfCadence: false",
                })}
              />
              <Badge
                color={colors.slate}
                text={intl.formatMessage({
                  id: "reports.map.header.activities.outOfCadence.legend.noContact",
                  defaultMessage: "No contact yet",
                  description: "Tooltip for pins of entities with outOfCadence: null",
                })}
              />
            </div>
          )}
          {filter === MapFilter.AUTO_ACTIVITIES && (
            <div className={styles.activityReliabilityLegend}>
              <Badge
                className={styles.stage}
                color={ReliabilityColor.RELIABLE}
                text={
                  <TextWithInfo
                    info={intl.formatMessage({
                      id: "reports.map.header.activities.reliability.legend.tooltip",
                      defaultMessage:
                        "User’s location was within 100m of the record when marked complete",
                      description: "Tooltip for the date range filter when it is disabled",
                    })}
                    inline
                    placement="bottom"
                  >
                    {intl.formatMessage({
                      id: "reports.map.header.activities.reliability.legend.verified",
                      defaultMessage: "Verified Check-In",
                      description: "Tooltip for the date range filter when it is disabled",
                    })}
                  </TextWithInfo>
                }
              />

              <Badge
                className={styles.stage}
                color={ReliabilityColor.NOT_RELIABLE}
                text={intl.formatMessage({
                  id: "reports.map.header.activities.reliability.legend.notVerified",
                  defaultMessage: "Not Verified",
                  description: "Tooltip for the date range filter when it is disabled",
                })}
              />
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export default ActivityReportHeader;
